import {
  About,
  AboutLoc,
  BlockListSection,
  BlockListSectionItem,
  BlockListSectionLoc,
  BlockListSectionPropLoc,
  BlockListSectionItemLoc,
  BlockListSectionItemPropLoc,
  ContentType,
  DetailSection,
  DetailSectionItem,
  DetailSectionLoc,
  DetailSectionPropLoc,
  DetailSectionItemLoc,
  DetailSectionItemPropLoc,
  InlineListSection,
  InlineListSectionLoc,
  InlineListSectionPropLoc,
  LegacyLocation,
  Resume,
  Section,
  SectionType,
  ValueOf,
} from '@sr/types';

export default class RSDK {
  readonly resume: Resume;

  constructor(resume: Resume) {
    this.resume = resume;
  }

  get fullName() {
    return `${this.aboutProp('firstName') || ''} ${
      this.aboutProp('lastName') || ''
    }`.trim();
  }

  get sectionList() {
    if (!this.resume.sectionsOrder) return [];
    return orderedMerge(this.resume.sectionsOrder, this.resume.sections);
  }

  get detailSectionList() {
    return filterByType(this.sectionList, 'detail');
  }

  get blockListSectionList() {
    return filterByType(this.sectionList, 'block-list');
  }

  get inlineListSectionList() {
    return filterByType(this.sectionList, 'inline-list');
  }

  get workSectionList() {
    return filterByContentType(this.detailSectionList, 'work-experience');
  }

  get educationSectionList() {
    return filterByContentType(this.detailSectionList, 'education');
  }

  get skillsSectionList() {
    return filterByContentType(this.inlineListSectionList, 'skills');
  }

  get itemList() {
    let items: (DetailSectionItem | BlockListSectionItem)[] = [];
    this.sectionList.forEach((section) => {
      if (
        section.type !== 'inline-list' &&
        section.items &&
        section.itemsOrder
      ) {
        items = [
          ...items,
          ...orderedMerge<DetailSectionItem | BlockListSectionItem>(
            section.itemsOrder,
            section.items
          ),
        ];
      }
    });
    return items;
  }

  get detailItemList() {
    return this.itemList.filter(
      (item) => item.type && item.type === 'detail'
    ) as DetailSectionItem[];
  }

  get blockListItemList() {
    return this.itemList.filter(
      (item) => item.type && item.type === 'block-list'
    ) as BlockListSectionItem[];
  }

  get workExperienceItemList() {
    let items: DetailSectionItem[] = [];
    this.sectionList.forEach((section) => {
      if (
        section.type === 'detail' &&
        section.contentType === 'work-experience' &&
        section.items &&
        section.itemsOrder
      ) {
        items = [
          ...items,
          ...orderedMerge<DetailSectionItem>(section.itemsOrder, section.items),
        ];
      }
    });
    return items;
  }

  get educationItemList() {
    let items: DetailSectionItem[] = [];
    this.sectionList.forEach((section) => {
      if (
        section.type === 'detail' &&
        section.contentType === 'education' &&
        section.items &&
        section.itemsOrder
      ) {
        items = [
          ...items,
          ...orderedMerge<DetailSectionItem>(section.itemsOrder, section.items),
        ];
      }
    });
    return items;
  }

  get profilePhoto() {
    const { profilePhotoUrl, profilePhotoSrc } = this.resume;
    const url = profilePhotoUrl || profilePhotoSrc;
    return url || '';
  }

  get hasProfilePhoto() {
    if (
      this.profilePhoto &&
      templateSupportsPhoto(this.resume.style.activeTemplateId)
    ) {
      return true;
    } else {
      return false;
    }
  }

  get activeCustomizations() {
    const { activeTemplateId, customizations } = this.resume.style;
    return customizations[activeTemplateId];
  }

  get activeTemplateSpacing() {
    const activeCustomizations = this.activeCustomizations;
    if (activeCustomizations) {
      return activeCustomizations.baseSize;
    }
    return undefined;
  }

  get about() {
    return this.resume.about;
  }

  aboutProp(key: keyof About) {
    return this.resume.about[key];
  }

  section(id: string): Section;
  section(id: string, type: 'detail'): DetailSection;
  section(id: string, type: 'block-list'): BlockListSection;
  section(id: string, type: 'inline-list'): InlineListSection;
  section(id: string, type?: SectionType) {
    const section = this.resume.sections[id];
    if (!section) throw new Error(`Section ${id} not found`);
    if (type === undefined) return section;
    if (type === 'detail' && isDetailSection(section)) {
      return section;
    }
    if (type === 'block-list' && isBlockListSection(section)) {
      return section;
    }
    if (type === 'inline-list' && isInlineListSection(section)) {
      return section;
    }
    throw new Error(`Section ${id} isn't of type ${type}`);
  }

  item(
    sectionId: string,
    itemId: string
  ): DetailSectionItem | BlockListSectionItem;
  item(sectionId: string, itemId: string, type: 'detail'): DetailSectionItem;
  item(
    sectionId: string,
    itemId: string,
    type: 'block-list'
  ): BlockListSectionItem;
  item(
    sectionId: string,
    itemId: string,
    type?: 'detail' | 'block-list'
  ): DetailSectionItem | BlockListSectionItem {
    if (type === 'detail') {
      const items = this.section(sectionId, 'detail').items;
      if (items) {
        return items[itemId];
      }
    }
    if (type === 'block-list') {
      const items = this.section(sectionId, 'block-list').items;
      if (items) {
        return items[itemId];
      }
    }
    if (type === undefined) {
      const section = this.section(sectionId);
      if (section.type !== 'inline-list' && section.items) {
        return section.items[itemId];
      }
    }
    throw new Error(`Unable to find item ${itemId} in section ${sectionId}`);
  }

  getData(location: AboutLoc): ValueOf<About>;
  // Get a section
  getData(location: DetailSectionLoc): DetailSection;
  getData(location: BlockListSectionLoc): BlockListSection;
  getData(location: InlineListSectionLoc): InlineListSection;
  // Get a section prop value
  getData(location: DetailSectionPropLoc): ValueOf<DetailSection>;
  getData(location: BlockListSectionPropLoc): ValueOf<BlockListSection>;
  getData(location: InlineListSectionPropLoc): ValueOf<InlineListSection>;
  // Get an item
  getData(location: DetailSectionItemLoc): DetailSectionItem;
  getData(location: BlockListSectionItemLoc): BlockListSectionItem;
  // Get an item prop value
  getData(location: DetailSectionItemPropLoc): ValueOf<DetailSectionItem>;
  getData(location: BlockListSectionItemPropLoc): ValueOf<BlockListSectionItem>;
  getData(location: LegacyLocation) {
    const loc = location;
    switch (loc.type) {
      case 'about-prop':
        return this.aboutProp(loc.aboutProp);
      case 'detail-section':
        return this.section(loc.sectionId, 'detail');
      case 'block-list-section':
        return this.section(loc.sectionId, 'block-list');
      case 'inline-list-section':
        return this.section(loc.sectionId, 'inline-list');
      case 'detail-section-prop':
        return this.section(loc.sectionId, 'detail')[loc.sectionProp];
      case 'block-list-section-prop':
        return this.section(loc.sectionId, 'block-list')[loc.sectionProp];
      case 'inline-list-section-prop':
        return this.section(loc.sectionId, 'inline-list')[loc.sectionProp];
      case 'detail-section-item':
        return this.item(loc.sectionId, loc.itemId, 'detail');
      case 'block-list-section-item':
        return this.item(loc.sectionId, loc.itemId, 'block-list');
      case 'detail-section-item-prop':
        return this.item(loc.sectionId, loc.itemId, 'detail')[loc.itemProp];
      case 'block-list-section-item-prop':
        return this.item(loc.sectionId, loc.itemId, 'block-list')[loc.itemProp];
    }
  }

  static getItemsFromSection(section: DetailSection): DetailSectionItem[];
  static getItemsFromSection(section: BlockListSection): BlockListSectionItem[];
  static getItemsFromSection(section: DetailSection | BlockListSection) {
    if (isDetailSection(section) && section.items && section.itemsOrder) {
      return orderedMerge(section.itemsOrder, section.items);
    }
    if (isBlockListSection(section) && section.items && section.itemsOrder) {
      return orderedMerge(section.itemsOrder, section.items);
    }
    throw new Error(`Can't get items for section ${section.sectionId}`);
  }

  // A short string ID that can uniquely identify any SR object or object[property]
  static getAboutPropSrId(prop: string): string {
    return `about-${prop}`;
  }
  static getCustomizationPropSrId(prop: string): string {
    return `customization-${prop}`;
  }
  static getSectionSrId(args: {
    sectionId: string;
    sectionType: SectionType;
    contentType: ContentType;
  }): string {
    const { sectionId, sectionType, contentType } = args;
    return `section-${removeDashes(sectionId)}-${removeDashes(
      sectionType
    )}-${removeDashes(contentType)}`;
  }
  static getSectionPropSrId(args: {
    sectionId: string;
    sectionType: SectionType;
    contentType: ContentType;
    prop: string;
  }): string {
    const { sectionId, sectionType, contentType, prop } = args;
    return `section-${removeDashes(sectionId)}-${removeDashes(
      sectionType
    )}-${removeDashes(contentType)}-${prop}`;
  }
  static getItemSrId(args: {
    itemId: string;
    sectionType: SectionType;
    contentType: ContentType;
  }): string {
    const { itemId, sectionType, contentType } = args;
    return `item-${removeDashes(itemId)}-${removeDashes(
      sectionType
    )}-${removeDashes(contentType)}`;
  }
  static getItemPropSrId(args: {
    itemId: string;
    sectionType: SectionType;
    contentType: ContentType;
    prop: string;
  }): string {
    const { itemId, sectionType, contentType, prop } = args;
    return `item-${removeDashes(itemId)}-${removeDashes(
      sectionType
    )}-${removeDashes(contentType)}-${prop}`;
  }

  // TODO: delete this once old rules are gone
  static getSrIdFromLocation(loc: LegacyLocation): string {
    switch (loc.type) {
      case 'about-prop':
        return RSDK.getAboutPropSrId(loc.aboutProp);
      case 'detail-section':
      case 'block-list-section':
      case 'inline-list-section':
        return '';
      case 'detail-section-prop':
      case 'block-list-section-prop':
      case 'inline-list-section-prop':
        return '';
      case 'detail-section-item':
      case 'block-list-section-item':
        return '';
      case 'detail-section-item-prop':
      case 'block-list-section-item-prop':
        return '';
    }
  }
}

// Filter a list of sections by type
function filterByType(list: Section[], type: 'detail'): DetailSection[];
function filterByType(list: Section[], type: 'block-list'): BlockListSection[];
function filterByType(
  list: Section[],
  type: 'inline-list'
): InlineListSection[];
function filterByType(list: Section[], type: SectionType) {
  const sections = list.filter((item) => item.type === type);
  switch (type) {
    case 'detail':
      return sections as DetailSection[];
    case 'block-list':
      return sections as BlockListSection[];
    case 'inline-list':
      return sections as InlineListSection[];
  }
}

function filterByContentType<T extends Section>(
  sections: T[],
  contentType: ContentType
): T[] {
  return sections.filter((section) => section.contentType === contentType);
}

// Merge an ordered key array and object with values (eg: itemsOrder and items)
function orderedMerge<T>(
  array: string[] | undefined,
  object: Record<string, T> | undefined
): T[] {
  if (array == null || object == null) {
    throw new Error('Missing required arguments');
  }
  return array
    .map((id) => {
      return object[id];
    })
    .filter((section) => section != null);
}

// Type assertion for sections
function isDetailSection(section: Section): section is DetailSection {
  return (section as DetailSection).type === 'detail';
}
function isBlockListSection(section: Section): section is BlockListSection {
  return (section as BlockListSection).type === 'block-list';
}
function isInlineListSection(section: Section): section is InlineListSection {
  return (section as InlineListSection).type === 'inline-list';
}

// TODO: this should be part of the templates package but I'm adding it here
// to avoid the complexity of importing it now.
function templateSupportsPhoto(templateId: string): boolean {
  return ['pender', 'seymore', 'eton', 'arbutus'].includes(templateId);
}

function removeDashes(value: string): string {
  return value.replace(/-/g, '');
}
